import React, { Fragment } from 'react'
import { MapFocusContext } from './MapFocusContext'
import { PopoverMenu, PopoverMenuItem, PopoverMenuHeading } from 'common/styleguide/PopoverMenu'
import { GoogleMap } from 'common/GoogleMap'
import { Maplet } from 'common/Maplet'
import { Marker } from 'common/Map/Marker'
import { RouteLine } from 'common/Map/RouteLine'
import { PhotoLayer } from 'common/Map/PhotoLayer'
import { POIv2, POI } from 'common/Map/POI'
import { settingsMessages } from 'common/MapV2/controls/SettingsMenu'
import styles from './Map.scss'
import Select from 'react-select'
import { CheckboxGroup } from 'common/styleguide/CheckboxGroup'
import { RadioGroup } from 'common/styleguide/RadioGroup'
import { Icon } from 'common/Icon'
import { tileServices } from 'lib/tileServices'
import { Msg, fm, useFormatMessage, featureNames, commonNouns, commonUnits } from 'lib/localization'
import p from 'prop-types'
import _ from 'lodash'
import cx from 'classnames'
import { OrderedMap, Record } from 'immutable'
import { injectIntl } from 'react-intl'

// default map settings
export const MapSettings = Record({
  showDistanceMarkers: false,
  showPOIs: true,
  showBikePaths: false,
  metricUnits: false,
  scrollWheelZoom: true
})

export class Map extends React.PureComponent {
  static propTypes = {
    width: p.string, // css width value, including units
    height: p.string.isRequired, // css height value, including units
    overlay: p.string, // specify overlay for controlled component and register a callback
    defaultOverlay: p.string, // overlay for un-controlled component
    onOverlayChanged: p.func, // overlay change func, you only get this in controlled mode
    onSegmentSelected: p.func,
    onSegmentUnselected: p.func,
    sampleGraph: p.bool, // show or hide the sample graph
    routes: p.array, // routes to show, as plain JSON
    metricUnits: p.bool, // use metric units?
    showPhotos: p.bool, // showPhotos,
    photos: p.array, // TODO get rid of showPhotos prop
    pois: p.array,
    highlightSlice: p.array,
    highlightPoint: p.object,
    children: p.node,
    onMapClick: p.func,
    suggestedBounds: p.array,
    boundsOptions: p.object,
    scrollWheelZoom: p.bool,
    zoomToHighlightSlice: p.bool,
    zoomOutOnDeselect: p.bool,
    showToolbar: p.bool,
    showOverlayPicker: p.bool,
    showNativeFullscreen: p.bool,
    experimental: p.bool // enable experimental features
  }

  static defaultProps = {
    defaultOverlay: 'rwgps',
    showToolbar: false,
    defaultHighlightPoint: null,
    sampleGraph: false,
    routes: [],
    metricUnits: false,
    height: '500px',
    showPhotos: false,
    photos: [],
    scrollWheelZoom: false,
    boundsOptions: { padding: [60, 60] },
    experimental: false,
    zoomToHighlightSlice: true,
    mapSettings: null,
    defaultMapSettings: MapSettings(),
    showOverlayPicker: true,
    showNativeFullscreen: false
  }

  constructor (props) {
    super(props)
    this.state = {
      localOverlay: props.defaultOverlay,
      localHighlightPoint: props.defaultHighlightPoint,
      localHighlightSlice: props.defaultHighlightSlice,
      localSettings: props.mapSettings || props.defaultMapSettings,
      focusContext: {
        focusId: null,
        setFocus: this.setFocus
      }
    }
  }

  UNSAFE_componentWillReceiveProps = (nextProps) => { // eslint-disable-line
    const oldIds = nextProps.routes.map(r => r.id)
    const newIds = this.props.routes.map(r => r.id)
    if (!_.isEqual(oldIds, newIds) || this.props.height !== nextProps.height) {
      // invalidate map viewport preservation to allow a bounds reset
      this._lastZoom = null
      this._lastCenter = null

      this.setState({
        localHighlightPoint: null,
        localHighlightSlice: null
      })
    }
  }

  onOverlayChanged = (newOverlay) => {
    this.props.onOverlayChanged &&
      this.props.onOverlayChanged(newOverlay)

    !this.props.overlay &&
      this.setState({ localOverlay: newOverlay })
  }

  onZoomCenterChanged = (zoom, center) => {
    this._lastZoom = zoom
    this._lastCenter = center
  }

  onSegmentSelected = (segment) => {
    this.props.onSegmentSelected &&
      this.props.onSegmentSelected(segment)

    !this.props.highlightSlice &&
      this.setState({ localHighlightSlice: segment })
  }

  onSegmentUnselected = () => {
    this.props.onSegmentUnselected &&
      this.props.onSegmentUnselected()

    !this.props.highlightSlice &&
      this.setState({ localHighlightSlice: null })
  }

  onPointHighlighted = (highlightPoint) => {
    this.props.onPointHighlighted &&
      this.props.onPointHighlighted(highlightPoint)

    !this.props.highlightPoint &&
      this.setState({ localHighlightPoint: highlightPoint })
  }

  setFocus = (focusId) => {
    const focusContext = {
      focusId,
      setFocus: this.setFocus
    }
    this.setState({ focusContext })
  }

  onMapClick = (e) => {
    this.setFocus(null)
    this.props.onMapClick && this.props.onMapClick(e)
  }

  onToggleSetting = (key) => {
    const { onToggleSetting } = this.props
    const { localSettings } = this.state

    const newSettings = localSettings.get(key)
      ? localSettings.set(key, false)
      : localSettings.set(key, true)

    onToggleSetting && onToggleSetting(key, newSettings)

    this.setState({ localSettings: newSettings })
  }

  render () {
    const filteredProps = _.pick(this.props, Object.keys(Map.propTypes))
    const { routes, pois, showNativeFullscreen } = this.props
    const { showToolbar, showOverlayPicker } = filteredProps
    const overlay = filteredProps.overlay || this.state.localOverlay
    const overlaySpec = tileServices[overlay] || tileServices['rwgps']
    const highlightSlice = filteredProps.highlightSlice || this.state.localHighlightSlice
    const highlightPoint = filteredProps.highlightPoint || this.state.localHighlightPoint
    const snapLatLng = overlaySpec.type !== 'google' && highlightPoint && routes[0] &&
      routes[0].trackPoints[highlightPoint.index]
    const { experimental, style, className } = this.props
    const { focusContext, localSettings } = this.state
    const mapSettings = this.props.mapSettings || localSettings
    const showDistanceMarkers = experimental && mapSettings.showDistanceMarkers

    const getPoiPhotos = (poi) => {
      return poi.pids?.map(pid => ({ 'id': pid }))
    }

    const childrenPlusExtras = <Fragment>
      {routes.length === 1 && highlightSlice && <RouteLine route={routes[0]}
        color='blue'
        slice={highlightSlice}
        showStartMarker={false}
        showEndMarker={false} />}
      {this.props.children}
      {snapLatLng && <Marker position={snapLatLng} icon='default' />}
      {mapSettings.showPOIs &&
        routes.length === 1 &&
        routes[0].pointsOfInterest &&
        routes[0].pointsOfInterest.map(poi => <POIv2 key={poi.id} poi={poi} photos={routes[0].photos.concat(getPoiPhotos(poi))} />)}
      {mapSettings.showPOIs && pois &&
        pois.map(p => <POI
          key={p.id}
          id={p.id}
          poiType={p.poiType}
          url={p.url}
          lat={p.lat}
          lng={p.lng}
          description={p.description}
          name={p.name} />)}
      <PhotoLayer photos={filteredProps.photos} />
    </Fragment>
    // problem: if we add a wrapper, we have to manage height ourselves

    return (
      <div className={cx(styles.Map, className)} style={{ height: filteredProps.height, width: filteredProps.width, ...style }}>
        <MapFocusContext.Provider value={focusContext}>
          {(!experimental) || overlaySpec.type === 'google'
            ? <GoogleMap {...filteredProps}
              showNativeFullscreen={showNativeFullscreen}
              showBikePaths={mapSettings.showBikePaths}
              showDistanceMarkers={showDistanceMarkers}
              onMapClick={this.onMapClick}
              overlay={overlay}
              showOverlayPicker={!experimental}
              onOverlayChanged={this.onOverlayChanged}
              onZoomCenterChanged={this.onZoomCenterChanged}
              lastCenter={this._lastCenter}
              lastZoom={this._lastZoom}
              scrollWheelZoom={mapSettings.scrollWheelZoom}
              onSegmentSelected={this.onSegmentSelected}
              onSegmentUnselected={this.onSegmentUnselected}
              highlightSlice={undefined} // explicitly set this to undefined so it can't be set by filteredProps
              onPointHighlighted={this.onPointHighlighted}
              height='100%'
              children={childrenPlusExtras} />
            : <Maplet {...filteredProps}
              showNativeFullscreen={showNativeFullscreen}
              showDistanceMarkers={showDistanceMarkers}
              onMapClick={this.onMapClick}
              overlay={overlay}
              highlightPoint={highlightPoint}
              highlightSlice={undefined} // explicitly set this to undefined so it can't be set by filteredProps
              onPointHighlighted={this.onPointHighlighted}
              onZoomCenterChanged={this.onZoomCenterChanged}
              scrollWheelZoom={mapSettings.scrollWheelZoom}
              lastCenter={this._lastCenter}
              lastZoom={this._lastZoom}
              onSegmentSelected={this.onSegmentSelected}
              onSegmentUnselected={this.onSegmentUnselected}
              height='100%'
              children={childrenPlusExtras} />}
          {experimental && showOverlayPicker && <OverlayPicker overlay={overlay} onOverlayChanged={this.onOverlayChanged} />}
          {showToolbar && <MapToolbar
            mapType={overlaySpec.type}
            mapSettings={mapSettings}
            onToggleSetting={this.onToggleSetting} />}
        </MapFocusContext.Provider>
      </div>
    )
  }
}

const OverlayPicker = ({ onOverlayChanged, overlay }) => {
  const fm = useFormatMessage()
  return <div className={cx(styles.widget, styles.OverlayPicker)}>
    <Select
      clearable={false}
      searchable={false}
      value={overlay}
      onChange={(o) => onOverlayChanged(o.value)}
      options={Object.values(tileServices).map(service => ({
        label: service.intlName ? fm(service.intlName) : service.name,
        value: service.id
      }))} />
  </div>
}

const overlays = intl => OrderedMap({
  showPOIs: fm(intl, featureNames.pointsOfInterest, { count: 0 }),
  showBikePaths: fm(intl, settingsMessages.bikePaths),
  showDistanceMarkers: fm(intl, settingsMessages.distanceMarkers)
})

const interfaceOptions = intl => OrderedMap({
  scrollWheelZoom: fm(intl, settingsMessages.zoomByScrolling)
})

const unitsOptions = intl => OrderedMap({
  imperial: fm(intl, commonUnits.imperial),
  metric: fm(intl, commonUnits.metric)
})

class _MapToolbar extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      isMounted: false,
      menuOpen: false
    }
  }

  componentDidMount () {
    this.setState({ isMounted: true })
  }

  toggle = () => {
    this.setState({ menuOpen: !this.state.menuOpen })
  }

  onChangeUnits = (selectedUnits) => {
    // this is hacky, but there's a *big* refactor needed in order to
    // make the <Map> component fully MetricUnitsContext-driven.
    //
    // For now, to unblock the metric units toggle on the RSP page,
    // we're going to set the mapSettings level metricUnits pref,
    // but not use it and rely on the serialization to currentUser.prefs
    // in order to toggle it...
    const { mapSettings, onToggleSetting } = this.props
    const metricUnits = selectedUnits === 'metric'
    if (mapSettings.metricUnits !== metricUnits) {
      onToggleSetting('metricUnits')
    }
  }

  render () {
    const { mapSettings, onToggleSetting, mapType, intl } = this.props
    const { isMounted, menuOpen } = this.state
    const activeSettings = mapSettings.toMap().filter(v => v).keySeq().toSet()

    // only show bike paths option for Google maps
    const maybeBikePaths = mapType === 'google'
      ? overlays(intl)
      : overlays(intl).remove('showBikePaths')

    return (
      <div className={cx(styles.widget, styles.MapToolbar)}>
        <button ref={r => { this._button = r }} onClick={this.toggle} className={cx(menuOpen && styles.active)}>
          <Icon i='ios-settings' /> <Msg m={featureNames.settings} />
        </button>
        {isMounted && menuOpen && <PopoverMenu
          intl={intl}
          target={this._button}
          container={window.body}
          placement='bottom'
          onRequestClose={() => this.setState({ menuOpen: false })} >
          <PopoverMenuHeading><Msg m={settingsMessages.mapOverlays} /></PopoverMenuHeading>
          <PopoverMenuItem>
            <CheckboxGroup style={{ margin: '0 0' }} horizontal options={maybeBikePaths} selected={activeSettings} onToggleItem={onToggleSetting} />
          </PopoverMenuItem>
          <PopoverMenuHeading><Msg m={commonNouns.options} /></PopoverMenuHeading>
          <PopoverMenuItem>
            <CheckboxGroup style={{ margin: '0 0' }} horizontal options={interfaceOptions(intl)} selected={activeSettings} onToggleItem={onToggleSetting} />
          </PopoverMenuItem>
          <PopoverMenuHeading><Msg m={commonNouns.units} /></PopoverMenuHeading>
          <PopoverMenuItem>
            <RadioGroup
              horizontal
              style={{ margin: '0 0' }}
              options={unitsOptions(intl)}
              selected={activeSettings.has('metricUnits') ? 'metric' : 'imperial'}
              onChange={this.onChangeUnits} />
          </PopoverMenuItem>
        </PopoverMenu>}
      </div>
    )
  }
}

const MapToolbar = injectIntl(_MapToolbar)
